import { useMemo } from 'react';

/**
 * Read/write access to a template element's `metadata` JSON string.
 *
 * Metadata is shared ground: pricing uses `fee`/`description`/`hst`, the fees
 * smart input uses `fixed_fee`/`hst`, and checkbox_sum uses `tag`/`weight`.
 * Every write therefore MERGES, so a helper that owns some keys can never
 * destroy the ones it doesn't know about.
 *
 * Metadata is hand-authored, so it may not parse. In that case `malformed` is
 * true and `patch` is inert — overwriting would silently discard whatever the
 * author was in the middle of typing. Callers should disable themselves and
 * leave the raw textarea as the way out.
 */
export const useElementMetadata = (element, updateField) => {
  const raw = element?.metadata;

  const { metadata, malformed } = useMemo(() => {
    if (!raw || !String(raw).trim()) return { metadata: {}, malformed: false };
    try {
      const parsed = JSON.parse(raw);
      // Arrays and primitives parse fine but can't carry named keys.
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        return { metadata: {}, malformed: true };
      }
      return { metadata: parsed, malformed: false };
    } catch (err) {
      return { metadata: {}, malformed: true };
    }
  }, [raw]);

  // A key set to undefined is removed rather than written as null, so clearing
  // a field leaves no orphan behind for the runtime to trip over.
  const patch = (changes) => {
    if (malformed) return;
    const next = { ...metadata };
    Object.entries(changes).forEach(([key, value]) => {
      if (value === undefined) delete next[key];
      else next[key] = value;
    });
    const metadataString = Object.keys(next).length ? JSON.stringify(next) : '';
    updateField(element.id, { metadata: metadataString });
  };

  return { metadata, malformed, patch };
};

/**
 * Metadata is hand-authored JSON, so a fee may arrive as a number or a quoted
 * string. Store a clean integer/decimal as a real number and anything else
 * (including a half-typed "25.") verbatim, so the field stays editable.
 */
export const toMetadataNumber = (raw) => {
  const trimmed = String(raw ?? '').trim();
  if (!trimmed) return undefined;
  const parsed = parseFloat(trimmed);
  return Number.isFinite(parsed) && String(parsed) === trimmed ? parsed : trimmed;
};

export default useElementMetadata;
